import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import isEqual from "lodash/isEqual";
import { useCallback, useEffect, useRef, useState } from "react";

import { ff } from "../../common/flag";
import { playSound } from "../../common/playSound";
import { AUTHENTIFICATION_MODE, CAPTCHA_BYPASS_KEY, LS_AUTH, LS_BADGE_CACHE, LS_USER_CACHE, REFRESH_GUEST_DELAY, REFRESH_USER_DELAY, RENEW_TOKEN_DELAY } from "../../config";
import { useLanguage } from "../../contexts/LanguageContext";
import soundChat from './assets/sound_chat.mp3';
import soundNotification from './assets/sound_notification.mp3';

import ChangeLanguageMutation from './ChangeLanguageMutation.graphql';
import GetMeQuery from './GetMeQuery.graphql';
import ObtainBadgeMutation from './ObtainBadgeMutation.graphql';
import RenewTokenMutation from './RenewTokenMutation.graphql';
import ResetPasswordApplyMutation from './ResetPasswordApplyMutation.graphql';
import SignInMutation from './SignInMutation.graphql';
import SignOutMutation from './SignOutMutation.graphql';
import SignUpMutation from './SignUpMutation.graphql';

const UNAUTHENTICATED_USER_STATE = {
    "data": {
        "me": null,
        "allowedActions": [
            "SIGN_IN",
            "SIGN_UP"
        ],
        "unreadNotifications": {
            "totalCount": 0,
            "__typename": "NotificationsConnection"
        },
        "unreadChats": {
            "totalCount": 0,
            "__typename": "UsersConnection"
        }
    }
};

const getFromLocalStorage = () => {
    const previousData = JSON.parse(localStorage.getItem(LS_USER_CACHE) || '{}') || {};
    return {
        data: previousData?.data || UNAUTHENTICATED_USER_STATE,
        __lastUpdate: previousData?.__lastUpdate || 0,
    };
}

const saveToLocalStorage = (data, updateLocalStorageTime = true) => {
    const newTime = (updateLocalStorageTime ? Math.floor(new Date().getTime() / 1000) : getFromLocalStorage().__lastUpdate) || 0;
    localStorage.setItem(LS_USER_CACHE, JSON.stringify({ data, __lastUpdate: newTime }));
}

export const useUserStore = () => {
    const { actions: languageActions } = useLanguage();
    const { refetch: getMeQuery } = useQuery(GetMeQuery, { skip: true });
    const [signOutMutation] = useMutation(SignOutMutation);
    const [signInMutation] = useMutation(SignInMutation);
    const [signUpMutation] = useMutation(SignUpMutation);
    const [resetPasswordApplyMutation] = useMutation(ResetPasswordApplyMutation);
    const [renewTokenMutation] = useMutation(RenewTokenMutation);
    const [changeLanguageMutation] = useMutation(ChangeLanguageMutation);
    const [obtainBadgeMutation] = useMutation(ObtainBadgeMutation);
    const [reactiveData, setReactiveData] = useState(getFromLocalStorage()?.data || UNAUTHENTICATED_USER_STATE);
    const dataRef = useRef(getFromLocalStorage());

    const refreshDelay = reactiveData?.me ? REFRESH_USER_DELAY : REFRESH_GUEST_DELAY;

    const client = useApolloClient();
    const setUserData = useCallback((rawData, updateTime = true) => {

        if (!isEqual(dataRef?.current?.data, rawData)) {
            setReactiveData(rawData || UNAUTHENTICATED_USER_STATE);
        }

        if (updateTime || !isEqual(dataRef?.current?.data, rawData)) {
            saveToLocalStorage(rawData, updateTime);
        }

        // Update data
        dataRef.current.data = rawData;

        // Update time if needed
        if (updateTime) {
            dataRef.current.__lastUpdate = Math.floor(new Date().getTime() / 1000);
        }

    }, []);

    const changeLanguage = useCallback(async (interfaceLanguage, contentLanguages, shouldUpdateUser = false) => {
        // Update app state
        languageActions.setCurrentInterfaceLanguage(interfaceLanguage);
        languageActions.setCurrentContentLanguages(contentLanguages);

        // If connected and infos differs, update user profile
        if (shouldUpdateUser && dataRef?.current?.data?.me?.id) {
            await changeLanguageMutation({
                variables: {
                    input: {
                        userId: dataRef.current?.data?.me?.id,
                        preferredLanguage: interfaceLanguage,
                        contentLanguages: contentLanguages,
                    },
                },
            }).catch(() => null);
        }
    }, [changeLanguageMutation, languageActions]);

    const refreshUser = useCallback(async (force = false) => {
        // If we already refresh the user data recently, we skip the refresh
        if (!force && dataRef.current.__lastUpdate + refreshDelay - 1 > Math.floor(new Date().getTime() / 1000)) {
            return;
        }

        // Fetch user informations
        let rawData = null
        try {
            rawData = await getMeQuery();
        } catch (err) {
            console.error(err);
        }

        // Sound notification for new notifications or private messages
        if (ff('sound-notifications') && rawData?.data?.me && rawData?.data?.me?.id === dataRef?.current?.data?.me?.id) {

            // Avoid duplicate sound if chat and notification are received at the same time
            let soundPlayed = false;

            // Chat sound effect
            if (!soundPlayed &&
                rawData?.data?.unreadChats?.totalCount > 0 &&
                rawData?.data?.unreadChats?.totalCount > dataRef?.current?.data?.unreadChats?.totalCount
            ) {
                playSound(soundChat);
                soundPlayed = true;
            }

            // Notification sound effect
            if (!soundPlayed &&
                rawData?.data?.unreadNotifications?.totalCount > 0 &&
                rawData?.data?.unreadNotifications?.totalCount > dataRef?.current?.data?.unreadNotifications?.totalCount
            ) {
                playSound(soundNotification);
                soundPlayed = true;
            }
        }

        // Check if received data is valid
        if (rawData !== null && (rawData?.data?.me || rawData?.data?.me === null) && (rawData?.data?.errors?.length || 0) === 0) {
            setUserData(rawData.data);
            if (rawData?.data?.me?.preferredLanguage) {
                languageActions.setCurrentInterfaceLanguage(rawData?.data?.me?.preferredLanguage);
            }
            if (rawData?.data?.me?.preferredContentLanguages) {
                languageActions.setCurrentContentLanguages(rawData?.data?.me?.preferredContentLanguages);
            }
        }
    }, [getMeQuery, setUserData, languageActions, refreshDelay]);

    const refreshToken = useCallback(async () => {
        if (!dataRef?.current?.data?.me?.id) {
            return;
        }

        const rawData = await renewTokenMutation({
            variables: {
                input: {
                    authentificationMode: AUTHENTIFICATION_MODE,
                },
            },
        });
        if (rawData?.data?.renewToken?.authentificationToken) {
            localStorage.setItem(LS_AUTH, rawData?.data?.renewToken?.authentificationToken);
        }
    }, [renewTokenMutation]);

    const handleCacheChange = useCallback((event) => {
        if (event.key === LS_USER_CACHE) {
            // Get info and push it in reactive data to trigger render
            const rawData = getFromLocalStorage();
            if (!isEqual(dataRef?.current?.data, rawData)) {
                setReactiveData(rawData?.data || UNAUTHENTICATED_USER_STATE);
            }

            // Force set value to keep the lastTime update from other tabs
            dataRef.current = rawData;

            // If language is defined, update it
            if (dataRef.current?.data?.me?.id) {
                changeLanguage(dataRef.current?.data?.me?.preferredLanguage, dataRef.current?.data?.me?.contentLanguages, false);
            }
        }
    }, [setReactiveData, changeLanguage]);

    // Handle cache changes when another tab update the user cache
    useEffect(() => {
        window.addEventListener("storage", handleCacheChange);
        return () => {
            window.removeEventListener("storage", handleCacheChange);
        }
    }, [handleCacheChange]);

    // Refresh user token
    useEffect(() => {
        if (AUTHENTIFICATION_MODE !== 'TOKEN') {
            return;
        }

        refreshToken();
        const clock = setInterval(() => {
            refreshToken();
        }, RENEW_TOKEN_DELAY * 1000);
        return () => clearInterval(clock);
    }, [refreshToken]);

    // Refetch user informations
    useEffect(() => {
        refreshUser();
        const clock = setInterval(() => {
            refreshUser();
        }, refreshDelay * 1000);
        return () => clearInterval(clock);
    }, [refreshUser, refreshDelay]);

    return {
        currentUser: reactiveData?.me || null,
        allowedActions: reactiveData?.allowedActions || [],
        unreadNotificationsCount: reactiveData?.unreadNotifications?.totalCount || 0,
        unreadChatsCount: reactiveData?.unreadChats?.totalCount || 0,
        changeLanguage: async (interfaceLanguage, contentLanguages) => {
            console.log("useUserStore::changeLanguage()", interfaceLanguage, contentLanguages);
            await changeLanguage(interfaceLanguage, contentLanguages, true);
            await refreshUser(true);
        },
        signIn: async (identifier, password, captchaToken) => {
            console.log("useUserStore::signIn()", { identifier, password, captchaToken });

            const signInData = await signInMutation({
                variables: {
                    input: {
                        identifier,
                        password,
                        token: CAPTCHA_BYPASS_KEY || captchaToken,
                        authentificationMode: AUTHENTIFICATION_MODE,
                    },
                },
            });

            // If success, we purge the cache and redirect to the homepage
            if (signInData?.data?.signIn?.user?.id) {
                if (AUTHENTIFICATION_MODE === 'TOKEN' && signInData?.data?.signIn?.authentificationToken) {
                    localStorage.setItem(LS_AUTH, signInData?.data?.signIn?.authentificationToken);
                }
                localStorage.removeItem(LS_USER_CACHE);
                await changeLanguage(signInData?.data?.signIn?.user?.preferredLanguage, signInData?.data?.signIn?.user?.contentLanguages);
                await client.resetStore();
                await refreshUser(true);
                return;
            }

            // Error
            return signInData?.error?.errors?.[0]?.extensions?.code;
        },
        signUp: async (login, email, password, preferredLanguage, agreeToReceiveEmails, captchaToken) => {
            const signUpData = await signUpMutation({
                variables: {
                    input: {
                        login,
                        email,
                        password: password,
                        preferredLanguage,
                        agreeToReceiveEmails,
                        token: CAPTCHA_BYPASS_KEY || captchaToken,
                        authentificationMode: AUTHENTIFICATION_MODE,
                    },
                },
            });

            // If success, we purge the cache and redirect to the homepage
            if (signUpData?.data?.signUp?.user?.id) {
                if (AUTHENTIFICATION_MODE === 'TOKEN' && signUpData?.data?.signUp?.authentificationToken) {
                    localStorage.setItem(LS_AUTH, signUpData?.data?.signUp?.authentificationToken);
                }
                localStorage.removeItem(LS_USER_CACHE);
                await client.resetStore();
                await refreshUser(true);
                return;
            }

            // Error
            return signUpData?.error?.errors?.[0]?.extensions?.code;
        },
        signOut: async () => {
            console.log("useUserStore::logout()");
            const signOutData = await signOutMutation();

            // If success, we purge the cache and redirect to the homepage
            if (signOutData?.data?.signOut === null) {
                if (AUTHENTIFICATION_MODE === 'TOKEN') {
                    localStorage.removeItem(LS_AUTH);
                }
                localStorage.removeItem(LS_USER_CACHE);
                localStorage.removeItem(LS_BADGE_CACHE);
                setUserData(UNAUTHENTICATED_USER_STATE);
                await client.resetStore();
            }
        },
        resetPassword: async (password, resetPasswordToken) => {
            console.log("useUserStore::resetPassword()");

            const passwordRecovery = await resetPasswordApplyMutation({
                variables: {
                    input: {
                        password,
                        resetPasswordToken,
                        authentificationMode: AUTHENTIFICATION_MODE,
                    },
                },
            });


            if (passwordRecovery?.data?.resetPasswordApply?.user?.id) {
                if (AUTHENTIFICATION_MODE === 'TOKEN' && passwordRecovery?.data?.resetPasswordApply?.authentificationToken) {
                    localStorage.setItem(LS_AUTH, passwordRecovery?.data?.resetPasswordApply?.authentificationToken);
                }
                localStorage.removeItem(LS_USER_CACHE);
                await client.resetStore();
                return;
            }

            // Error
            return passwordRecovery?.error?.errors?.[0]?.extensions?.code;
        },
        refetch: async () => {
            console.log("useUserStore::refetch()");
            await refreshUser(true);
        },
        obtainBadge: async (badgeId) => {
            console.log("useUserStore::obtainBadge()", badgeId);

            // Not connected, skip
            if (!dataRef?.current?.data?.me?.id) {
                return;
            }

            // Already received in the current session, skip
            const badges = JSON.parse(localStorage.getItem(LS_BADGE_CACHE) || '[]');
            if (badges.includes(badgeId)) {
                return;
            }

            // Try to obtain the badge, and if success, add it to the cache to avoid multiple calls in the same session
            try {
                const raw = await obtainBadgeMutation({
                    variables: { input: { badgeId } }
                });

                if (!raw?.errors) {
                    localStorage.setItem(LS_BADGE_CACHE, JSON.stringify([...badges, badgeId]));
                }
            } catch (err) { } // eslint-disable-line no-empty
        }
    }
}